import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Snapshot, ProductsData } from '../model/types';
import { normalizeProducts } from '../model/model';

/**
 * The catalog slice the model reads: Systems (registry join and catalog-only
 * products), Components (repository links and the directory count) and
 * Domains (grouping catalog-only products). Queried as the signed-in user,
 * so catalog permissions apply.
 */
const CATALOG_KINDS = ['System', 'Component', 'Domain'];
const CATALOG_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'metadata.annotations',
  'spec.owner',
  'spec.domain',
];

export interface ProductsApi {
  load(signal?: AbortSignal): Promise<ProductsData>;
}
export const productsApiRef = createApiRef<ProductsApi>({
  id: 'plugin.products.api',
});
export class HubProductsApi implements ProductsApi {
  constructor(
    private readonly deps: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
      catalogApi: CatalogApi;
    },
  ) {}
  async load(signal?: AbortSignal): Promise<ProductsData> {
    const url = await this.deps.discoveryApi.getBaseUrl('products');
    const response = await this.deps.fetchApi.fetch(`${url}/snapshot`, {
      signal,
    });
    if (!response.ok)
      throw new Error(
        `The product registry could not be loaded (${response.status}).`,
      );
    const snapshot = (await response.json()) as Snapshot;
    if (
      snapshot.schemaVersion !== 1 ||
      !Array.isArray(snapshot.registry?.portfolios)
    )
      throw new Error('The product registry format is not supported.');
    let entities: Entity[] = [];
    let catalogError: string | undefined;
    try {
      let cursor: string | undefined;
      const seen = new Set<string>();
      do {
        if (signal?.aborted)
          throw new DOMException('Request cancelled', 'AbortError');
        const page = await this.deps.catalogApi.queryEntities(
          cursor
            ? { cursor, limit: 200, fields: CATALOG_FIELDS }
            : {
                filter: { kind: CATALOG_KINDS },
                limit: 200,
                fields: CATALOG_FIELDS,
              },
        );
        entities.push(...page.items);
        cursor = page.pageInfo.nextCursor;
        if (cursor && seen.has(cursor))
          throw new Error('Catalog pagination did not advance.');
        if (cursor) seen.add(cursor);
      } while (cursor);
    } catch (error) {
      if (signal?.aborted) throw error;
      entities = [];
      catalogError =
        'Catalog lookup is unavailable. Product records are shown, but registration and entity links could not be checked.';
    }
    return {
      snapshot,
      entities,
      catalogError,
      products: normalizeProducts(snapshot, entities),
    };
  }
}
